import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const GlassButton = ({ children, variant = 'primary', icon, loading, disabled, onClick, className = '' }) => {
  const base = 'inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer';
  const variants = {
    primary: 'glassmorphic-button text-white hover:scale-[1.02]',
    outline: 'bg-white/[0.05] border border-white/10 text-white/70 hover:text-white hover:border-white/20 hover:bg-white/[0.08]',
  };
  return (
    <button onClick={onClick} disabled={disabled || loading} className={`${base} ${variants[variant]} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      {loading ? <Icon name="Loader2" size={16} className="animate-spin" /> : icon && <Icon name={icon} size={16} />}
      {children}
    </button>
  );
};

const GlassToggle = ({ label, description, checked, onChange }) => (
  <label className="flex items-start gap-3 cursor-pointer group py-2">
    <div className="relative flex-shrink-0 mt-0.5">
      <input type="checkbox" checked={checked} onChange={onChange} className="sr-only" />
      <div
        className={`w-10 h-5 rounded-full transition-all duration-300 ${checked ? '' : 'bg-white/10'}`}
        style={checked ? { background: 'linear-gradient(135deg, rgba(254, 131, 56, 0.6), rgba(236, 103, 30, 0.5))', boxShadow: '0 0 12px rgba(254, 131, 56, 0.3)' } : {}}
      >
        <div className={`absolute top-0.5 w-4 h-4 rounded-full transition-all duration-300 ${checked ? 'left-[22px] bg-white' : 'left-0.5 bg-white/50'}`} />
      </div>
    </div>
    <div className="flex-1">
      <p className="text-sm font-medium text-white group-hover:text-white/90">{label}</p>
      {description && <p className="text-xs text-white/40 mt-0.5">{description}</p>}
    </div>
  </label>
);

const NotificationsSection = () => {
  const [emailNotifications, setEmailNotifications] = useState({
    projectUpdates: true, renderComplete: true, subscriptionAlerts: true,
    billingReminders: true, newFeatures: false, promotions: false,
    weeklyDigest: true, securityAlerts: true
  });

  const [pushNotifications, setPushNotifications] = useState({
    projectUpdates: true, renderComplete: true, subscriptionAlerts: false,
    billingReminders: false, newFeatures: false
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleEmailChange = (key) => setEmailNotifications(prev => ({ ...prev, [key]: !prev?.[key] }));
  const handlePushChange = (key) => setPushNotifications(prev => ({ ...prev, [key]: !prev?.[key] }));

  const handleSave = async () => {
    setIsSaving(true);
    setTimeout(() => { setIsSaving(false); alert('Notification preferences saved successfully!'); }, 1000);
  };

  const handleEnableAll = () => {
    setEmailNotifications(Object.keys(emailNotifications).reduce((a, k) => ({ ...a, [k]: true }), {}));
    setPushNotifications(Object.keys(pushNotifications).reduce((a, k) => ({ ...a, [k]: true }), {}));
  };

  const handleDisableAll = () => {
    setEmailNotifications(Object.keys(emailNotifications).reduce((a, k) => ({ ...a, [k]: false }), {}));
    setPushNotifications(Object.keys(pushNotifications).reduce((a, k) => ({ ...a, [k]: false }), {}));
  };

  return (
    <div className="space-y-6">
      {/* Email Notifications */}
      <div className="glassmorphic-card rounded-2xl p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">Email Notifications</h2>
            <p className="text-sm text-white/40 mt-1">Choose what updates you want to receive via email</p>
          </div>
          <div className="flex gap-2">
            <GlassButton variant="outline" onClick={handleEnableAll}>Enable All</GlassButton>
            <GlassButton variant="outline" onClick={handleDisableAll}>Disable All</GlassButton>
          </div>
        </div>

        <div className="space-y-2">
          <div className="pb-4 mb-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <h3 className="text-xs font-semibold text-orange-400/80 uppercase tracking-wider mb-3">Project Activity</h3>
            <GlassToggle label="Project Updates" description="Get notified when your projects are processed or updated" checked={emailNotifications.projectUpdates} onChange={() => handleEmailChange('projectUpdates')} />
            <GlassToggle label="Render Complete" description="Receive alerts when your renders are finished processing" checked={emailNotifications.renderComplete} onChange={() => handleEmailChange('renderComplete')} />
          </div>

          <div className="pb-4 mb-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <h3 className="text-xs font-semibold text-orange-400/80 uppercase tracking-wider mb-3">Account & Billing</h3>
            <GlassToggle label="Subscription Alerts" description="Important updates about your subscription status" checked={emailNotifications.subscriptionAlerts} onChange={() => handleEmailChange('subscriptionAlerts')} />
            <GlassToggle label="Billing Reminders" description="Reminders about upcoming payments and invoices" checked={emailNotifications.billingReminders} onChange={() => handleEmailChange('billingReminders')} />
            <GlassToggle label="Security Alerts" description="Critical security notifications about your account" checked={emailNotifications.securityAlerts} onChange={() => handleEmailChange('securityAlerts')} />
          </div>

          <div>
            <h3 className="text-xs font-semibold text-orange-400/80 uppercase tracking-wider mb-3">Platform Updates</h3>
            <GlassToggle label="New Features" description="Be the first to know about new tools and capabilities" checked={emailNotifications.newFeatures} onChange={() => handleEmailChange('newFeatures')} />
            <GlassToggle label="Promotions & Offers" description="Special deals and promotional offers" checked={emailNotifications.promotions} onChange={() => handleEmailChange('promotions')} />
            <GlassToggle label="Weekly Digest" description="Summary of your activity and platform updates" checked={emailNotifications.weeklyDigest} onChange={() => handleEmailChange('weeklyDigest')} />
          </div>
        </div>
      </div>

      {/* Push Notifications */}
      <div className="glassmorphic-card rounded-2xl p-6 md:p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">Push Notifications</h2>
            <p className="text-sm text-white/40 mt-1">Receive instant notifications in your browser</p>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Bell" size={18} className="text-orange-400" />
            <span className="text-sm font-medium text-emerald-400">Enabled</span>
          </div>
        </div>

        <div className="space-y-1">
          <GlassToggle label="Project Updates" description="Real-time updates about your projects" checked={pushNotifications.projectUpdates} onChange={() => handlePushChange('projectUpdates')} />
          <GlassToggle label="Render Complete" description="Instant notification when renders finish" checked={pushNotifications.renderComplete} onChange={() => handlePushChange('renderComplete')} />
          <GlassToggle label="Subscription Alerts" description="Important subscription notifications" checked={pushNotifications.subscriptionAlerts} onChange={() => handlePushChange('subscriptionAlerts')} />
          <GlassToggle label="Billing Reminders" description="Payment and billing notifications" checked={pushNotifications.billingReminders} onChange={() => handlePushChange('billingReminders')} />
          <GlassToggle label="New Features" description="Announcements about new platform features" checked={pushNotifications.newFeatures} onChange={() => handlePushChange('newFeatures')} />
        </div>

        <div className="mt-6 p-4 rounded-xl" style={{ background: 'rgba(254, 131, 56, 0.05)', border: '1px solid rgba(254, 131, 56, 0.1)' }}>
          <div className="flex items-start gap-3">
            <Icon name="Info" size={18} className="text-orange-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-white mb-1">Browser Permissions Required</p>
              <p className="text-xs text-white/40">Make sure you've allowed notifications in your browser settings to receive push notifications.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <GlassButton icon="Save" loading={isSaving} onClick={handleSave}>Save Preferences</GlassButton>
        <GlassButton variant="outline">Reset to Defaults</GlassButton>
      </div>
    </div>
  );
};

export default NotificationsSection;
